import { createSnowplowTracker, getSnowplowTracker, HudlDataSchemas } from '@hudl/analytics';
import { Logger } from 'hudl-base';
import {
  CONSENT_CATEGORIES,
  hasConsentFor,
  subscribeToConsentChanges,
  isConsentDetermined,
} from './consent-utils';

export const SNOWPLOW_MIN_VISIT_LENGTH = 120; // seconds
export const SNOWPLOW_HEARTBEAT_INTERVAL = 120; // seconds

const TRACKER_ID = 'profile';

export class MediaSnowplowManager {
  static _initialized = false;
  static _context;
  static _logger = new Logger('MediaSnowplowManager');
  static _unsubscribeConsent = null;
  static _hasPerformanceConsent = false;

  static initialize = mediaSnowplowTrackerContext => {
    if (!mediaSnowplowTrackerContext.isTrackerEnabled || this._initialized) {
      return;
    }
    this._context = mediaSnowplowTrackerContext;

    if (isConsentDetermined()) {
      this._hasPerformanceConsent = hasConsentFor(CONSENT_CATEGORIES.PERFORMANCE);
    }

    const { userId, isProduction } = this._context;
    const env = isProduction ? 'prod' : 'thor';
    const effectiveUserId = this._hasPerformanceConsent && userId ? String(userId) : undefined;

    createSnowplowTracker({
      id: TRACKER_ID,
      platform: 'web',
      env,
      userID: effectiveUserId,
    });

    this._initialized = true;

    if (!this._hasPerformanceConsent) {
      getSnowplowTracker(TRACKER_ID).enableAnonymousTracking();
    }

    this._unsubscribeConsent = subscribeToConsentChanges(activeGroups => {
      const newHasPerformance = activeGroups.includes(CONSENT_CATEGORIES.PERFORMANCE);

      if (newHasPerformance !== this._hasPerformanceConsent) {
        this._hasPerformanceConsent = newHasPerformance;
        this.updateTrackingMode(newHasPerformance);
      }
    });
  };

  static updateTrackingMode = hasPerformanceConsent => {
    if (!this._initialized) {
      return;
    }

    const tracker = getSnowplowTracker(TRACKER_ID);

    if (hasPerformanceConsent) {
      tracker.disableAnonymousTracking();
      const userId = this._context?.userId;
      tracker.setUserID(userId ? String(userId) : undefined);
    } else {
      tracker.enableAnonymousTracking();
    }
  };

  static trackPagePings = () => {
    if (!this._initialized) {
      return;
    }
    getSnowplowTracker(TRACKER_ID).trackPagePings(SNOWPLOW_MIN_VISIT_LENGTH, SNOWPLOW_HEARTBEAT_INTERVAL);
  };

  static _buildUserContexts = () => {
    const { userId, role, backdooredUserId } = this._context;

    return userId && this._hasPerformanceConsent
      ? [
          HudlDataSchemas.UserEntity.V4_0_3.describe({
            user_id: String(userId),
            role: role || undefined,
            backdoored_user_id: backdooredUserId ? String(backdooredUserId) : null,
          }),
        ]
      : [];
  };

  static _buildPageContext = (pageType, pageSubType) => {
    return HudlDataSchemas.PageEntity.V2_0_0.describe({
      page_url: window.location.href,
      page_type: pageType,
      page_sub_type: pageSubType || undefined,
    });
  };

  static trackPage = (screenTrackingPageName, pageSubType) => {
    if (!this._initialized || !this._context) {
      return;
    }

    const contexts = [
      ...this._buildUserContexts(),
      this._buildPageContext(screenTrackingPageName, pageSubType),
    ];

    getSnowplowTracker(TRACKER_ID).trackPageViewEvent(screenTrackingPageName, contexts);
  };

  static trackContentFavoriteEventForAthlete = (actionType, favoritedLocation, athleteEntity, pageType) => {
    if (!this._initialized || !this._context) {
      return;
    }

    if (!athleteEntity.userId) {
      this._logger.log('trackContentFavoriteEventForAthlete called without a userId on athleteEntity', athleteEntity);
      return;
    }

    const event = HudlDataSchemas.ContentFavoriteEvent.V2_0_0.describe({
      action_type: actionType,
      favorited_location: favoritedLocation,
      content_name: '',
    });

    const contexts = [
      HudlDataSchemas.ApplicationEntity.V1_0_0.describe({
        app_name: 'Hudl',
      }),
      this._buildPageContext(pageType),
      HudlDataSchemas.ContentUserEntity.V1_0_0.describe({
        user_id: String(athleteEntity.userId),
        user_name: athleteEntity.userName,
      }),
      ...this._buildUserContexts(),
    ];

    getSnowplowTracker(TRACKER_ID).trackSelfDescribingEvent(event, contexts);
  };
}
